/**
 * D-ID client tools that let the avatar bring the scenario's learner document
 * up on screen mid-conversation, browse it, read it, and put it away again.
 *
 * D-ID has no native presentation feature: the tools are declared with
 * `config.type: "client"`, so when the LLM calls them the handler runs in the
 * learner's browser (`DidFrame`) and drives our own split-screen layout and
 * PDF viewer. No tool takes a document argument — each scenario presents
 * exactly one document, the one configured as its display source.
 */
import { did } from "./client";
import {
  CLOSE_DOCUMENT_TOOL,
  PRESENTATION_TOOL_DESCRIPTIONS,
  PRESENT_DOCUMENT_TOOL,
  READ_PAGE_TOOL,
  SHOW_PAGE_ARG,
  SHOW_PAGE_TOOL,
  presentationInstruction,
} from "../presentationTools";

// Provider-prefixed aliases kept for call sites; the contract itself is shared.
export const DID_PRESENT_DOCUMENT_TOOL = PRESENT_DOCUMENT_TOOL;
export const DID_CLOSE_DOCUMENT_TOOL = CLOSE_DOCUMENT_TOOL;
export const DID_SHOW_PAGE_TOOL = SHOW_PAGE_TOOL;
export const DID_READ_PAGE_TOOL = READ_PAGE_TOOL;

type ToolSpec = {
  name: string;
  description: string;
  /** JSON-schema properties; empty for the no-argument tools. */
  properties: Record<string, unknown>;
  required?: string[];
};

function spec(name: string, properties: Record<string, unknown> = {}, required?: string[]): ToolSpec {
  return {
    name,
    description: PRESENTATION_TOOL_DESCRIPTIONS[name],
    properties,
    ...(required ? { required } : {}),
  };
}

const PRESENT = spec(DID_PRESENT_DOCUMENT_TOOL);
const CLOSE = spec(DID_CLOSE_DOCUMENT_TOOL);
const SHOW_PAGE = spec(
  DID_SHOW_PAGE_TOOL,
  {
    [SHOW_PAGE_ARG.name]: {
      type: "integer",
      minimum: 1,
      description: SHOW_PAGE_ARG.prompt,
    },
  },
  [SHOW_PAGE_ARG.name],
);
const READ_PAGE = spec(DID_READ_PAGE_TOOL);



/** Tools attached whenever the capability is on. */
const BASE_TOOLS: ToolSpec[] = [PRESENT, CLOSE];
/** Paging tools only make sense for a PDF, the one kind our viewer controls. */
const PDF_TOOLS: ToolSpec[] = [SHOW_PAGE, READ_PAGE];

function toolDefinition(spec: ToolSpec, scenarioName?: string) {
  return {
    name: spec.name,
    description: spec.description,
    provider: (scenarioName || "Scenario").slice(0, 60),
    schema: {
      parameters: {
        type: "object",
        properties: spec.properties,
        ...(spec.required?.length ? { required: spec.required } : {}),
        additionalProperties: false,
      },
    },
    config: { type: "client" },
  } satisfies Record<string, unknown>;
}

/** Shared wording (see `providers/presentationTools.ts`). */
export const didPresentationInstruction = presentationInstruction;



/** Back-compat export for callers that only need the base instruction. */
export const DID_PRESENTATION_INSTRUCTION = didPresentationInstruction(false);

export interface ReconciledPresentationTools {
  /** Tool ids to attach to the agent (empty when the capability is off). */
  toolIds: string[];
  /** Tool ids to delete upstream after the scenario row is written. */
  retiredToolIds: string[];
}

/**
 * Create or refresh one tool, minting a replacement when the stored id is
 * gone upstream.
 */
async function upsertTool(
  spec: ToolSpec,
  existingId: string | undefined,
  scenarioName?: string,
): Promise<string> {
  const definition = toolDefinition(spec, scenarioName);
  if (existingId) {
    try {
      await did.updateTool({ tool_id: existingId, tool: definition });
      return existingId;
    } catch {
      // The tool was deleted upstream (or is otherwise unusable) — mint a new
      // one rather than saving an agent that points at a dead tool id.
    }
  }
  const created = await did.createTool(definition);
  if (!created.tool_id) {
    throw new Error(`D-ID did not return a tool id for the ${spec.name} tool`);
  }
  return created.tool_id;
}

/**
 * Reconcile the scenario's presentation tools with the trainer's toggle and the
 * display source kind. `existingToolIds` carries whatever we minted last save
 * (in tool order), so a re-save updates in place instead of piling up tools.
 */
export async function reconcilePresentationTool(input: {
  enabled: boolean;
  /** Previously minted ids, positionally matched to the tool list. */
  existingToolIds?: string[];
  /** Whether the display source is a PDF (enables the paging tools). */
  pageable?: boolean;
  scenarioName?: string;
}): Promise<ReconciledPresentationTools> {
  const existing = (input.existingToolIds ?? []).map((v) => v?.trim()).filter((v): v is string => !!v);

  if (!input.enabled) {
    return { toolIds: [], retiredToolIds: existing };
  }

  const specs = input.pageable ? [...BASE_TOOLS, ...PDF_TOOLS] : BASE_TOOLS;
  const toolIds: string[] = [];
  for (let i = 0; i < specs.length; i++) {
    toolIds.push(await upsertTool(specs[i], existing[i], input.scenarioName));
  }
  // Anything we owned beyond the current tool list (e.g. the paging pair after
  // the display source changed away from a PDF) is retired.
  const retiredToolIds = existing.filter((id) => !toolIds.includes(id));
  return { toolIds, retiredToolIds };
}

/** Best-effort upstream cleanup; a stray tool must never fail a save. */
export async function deletePresentationTool(toolId: string): Promise<void> {
  try {
    await did.deleteTool(toolId);
  } catch {
    /* ignore */
  }
}
